<template>
  <div class="query-results" :class="{ compact }">
    <EmptyState 
      v-if="!hasData"
      icon="search_off"
      :title="$t('No results found')"
      :description="$t('Try a different location or question')"
      :compact="compact"
    />

    <template v-else>
      <!-- COMPACT MODE -->
      <template v-if="compact">
        <div class="compact-header">
          <span class="compact-title">{{ $t(title) }}</span>
          <q-btn flat dense round icon="open_in_full" size="xs" @click="$emit('expand')" />
        </div>
        
        <div class="compact-body">
          <TrendCard v-if="showTrend" :trend="result.data.trend" compact class="compact-trend" />
          
          <component 
            :is="visualizationComponent"
            v-bind="visualizationProps"
            compact
          />
        </div>
      </template>

      <!-- FULL MODE -->
      <template v-else>
        <TrendCard v-if="showTrend" :trend="result.data.trend" class="q-mb-md" />
        
        <component 
          :is="visualizationComponent"
          v-bind="visualizationProps"
        />

        <div class="collection-info q-mt-md">
          <div class="info-title">{{ result.data.collection.title }}</div>
          <div class="info-description">{{ result.data.collection.description }}</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import EmptyState from '@/common/components/ui/empty-state.vue'
import SingleValueDisplay from '@/common/components/visualizations/single-value.vue'
import TimeSeriesChart from '@/common/components/visualizations/time-series-chart.vue'
import BoxPlot from '@/common/components/visualizations/box-plot.vue'
import PieChart from '@/common/components/visualizations/pie-chart.vue'
import TrendCard from '@/common/components/visualizations/trend-card.vue'
import { CLASS_COLORS } from '@/common/mixins/colors'

export default {
  name: 'QueryResults',
  
  components: {
    EmptyState,
    SingleValueDisplay,
    TimeSeriesChart,
    BoxPlot,
    PieChart,
    TrendCard
  },
  
  props: {
    result: { type: Object, required: true },
    compact: { type: Boolean, default: false },
    title: { type: String, default: '' }
  },

  emits: ['expand'],
  
  data() {
    return {
      classNames: {
        'sealed': 'Sealed Surface',
        'permanent herbaceous': 'Grassland',
        'woodland': 'Forest',
        'calcaric fluvisols': 'Calcaric Fluvisols',
        'eutric cambisols': 'Eutric Cambisols',
        'calcic cambisols': 'Calcic Cambisols',
        'folic leptosols': 'Folic Leptosols',
        'haplic luvisols': 'Haplic Luvisols'
      }
    }
  },
  
  computed: {
    hasData() {
      return this.result?.data
    },

    isPoint() {
      return this.result.data.geometry.type === 'Point'
    },
    
    isContinuous() {
      return this.result.data.collection.continuous
    },
    
    hasTimeSeries() {
      return this.result.data.data.length > 1
    },
    
    showTrend() {
      return this.result.data.trend != null
    },
    
    visualizationComponent() {
      if (this.isContinuous) {
        if (this.hasTimeSeries) return 'TimeSeriesChart'
        return this.isPoint ? 'SingleValueDisplay' : 'BoxPlot'
      }
      
      const coverageData = this.getCategoryData()
      return this.isPoint || coverageData.length <= 1 ? 'SingleValueDisplay' : 'PieChart'
    },
    
    visualizationProps() {
      const common = { unit: this.result.data.collection.unit }
      
      const propsMap = {
        SingleValueDisplay: {
          ...common,
          value: this.currentValue,
          className: this.getClassName(this.currentValue),
          colorMap: CLASS_COLORS
        },
        TimeSeriesChart: {
          ...common,
          data: this.result.data.data,
          title: this.result.data.collection.title,
          decimals: 2
        },
        BoxPlot: {
          ...common,
          stats: this.boxPlotStats,
          decimals: 2
        },
        PieChart: {
          data: this.getCategoryData(),
          colorMap: CLASS_COLORS,
          labelFormatter: cls => this.getClassName(cls)
        }
      }
      
      return propsMap[this.visualizationComponent] || {}
    },

    currentValue() {
      const lastItem = this.result?.data?.data?.at(-1)
      if (!lastItem) return null
      return lastItem.value ?? lastItem.mean ?? lastItem.dominant_cls ?? null
    },

    boxPlotStats() {
      const lastItem = this.result?.data?.data?.at(-1)
      if (!lastItem?.mean) return {}
      
      return {
        min: lastItem.p05 ?? (lastItem.mean - 2 * lastItem.stdev),
        q1: lastItem.mean - (lastItem.stdev * 0.675),
        median: lastItem.median,
        q3: lastItem.mean + (lastItem.stdev * 0.675),
        max: lastItem.p95 ?? (lastItem.mean + 2 * lastItem.stdev),
        mean: lastItem.mean,
        stdev: lastItem.stdev,
        count: this.result.data.data.length
      }
    }
  },
  
  methods: {
    getCategoryData() {
      const lastItem = this.result?.data?.data?.at(-1)
      if (!lastItem?.coverage) return []
      
      return lastItem.coverage
        .map(item => ({ class: item.cls, percentage: item.percent.toFixed(1) }))
        .sort((a, b) => b.percentage - a.percentage)
    },
    
    getClassName(value) {
      if (this.classNames[value]) return this.classNames[value]
      if (typeof value !== 'string') return value
      
      return value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.query-results {
  width: 100%;

  &.compact {
    background: white;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    padding: 0.5rem;
    min-width: 8rem;
    max-width: 12rem;

    body.body--dark & {
      background: $dark;
    }
  }
}

// ================================
// COMPACT MODE
// ================================
.compact-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.375rem;
}

.compact-title {
  font-size: 0.6875rem;
  font-weight: 500;
  color: $grey-7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 7rem;

  body.body--dark & {
    color: $grey-5;
  }
}

.compact-body {
  position: relative;
}

.compact-trend {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

// ================================
// FULL MODE
// ================================
.collection-info {
  background: $grey-2;
  border-radius: var(--radius-sm);
  padding: 0.75rem;
  border-left: 4px solid $primary;

  body.body--dark & {
    background: $grey-9;
    border-left-color: $secondary;
  }
}

.info-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: $grey-9;

  body.body--dark & {
    color: $grey-3;
  }
}

.info-description {
  font-size: 0.75rem;
  color: $grey-7;

  body.body--dark & {
    color: $grey-5;
  }
}
</style>